import React, { useState, useRef, useCallback, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsApi } from '../../lib/api';
import useMatchChatsUnread from '../../lib/useMatchChatsUnread';
import useFriendsUnread from '../../lib/useFriendsUnread';
import useMarketBoardUnread from '../../lib/useMarketBoardUnread';
import useMediationsCount from '../../lib/useMediationsCount';
import { useFloatingChat } from '../../contexts/FloatingChatContext';
import { isPushSupported, isCurrentlySubscribed, subscribeToPush, unsubscribeFromPush } from '../../lib/pushNotifications';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Bell,
  BellRing,
  LogOut,
  User,
  Users,
  Globe,
  Check,
  Menu,
  LayoutDashboard,
  Shield,
  ShoppingCart,
  Calendar,
  Trophy,
  MessageSquare,
  Settings,
  Crown,
  Search,
  X,
  Coins,
  FileText,
  BarChart3,
  ShieldCheck,
  BookOpen,
  Swords,
  ChevronDown,
  ChevronRight,
  LifeBuoy,
  ArrowLeftRight,
} from 'lucide-react';
import DigitalClock from '../DigitalClock';
import { toast } from 'sonner';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin, isSuperAdmin, isCaptain } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [viewedNotifications, setViewedNotifications] = useState(new Set());
  const [tourneyOpenHdr, setTourneyOpenHdr] = useState(() => {
    try { return localStorage.getItem('header.tournamentsOpen') !== 'false'; } catch { return true; }
  });
  const toggleTourneyHdr = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setTourneyOpenHdr((v) => {
      const nv = !v;
      try { localStorage.setItem('header.tournamentsOpen', String(nv)); } catch {}
      return nv;
    });
  };
  const [adminOpenHdr, setAdminOpenHdr] = useState(() => {
    try { return localStorage.getItem('header.adminOpen') !== 'false'; } catch { return true; }
  });
  const toggleAdminHdr = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setAdminOpenHdr((v) => {
      const nv = !v;
      try { localStorage.setItem('header.adminOpen', String(nv)); } catch {}
      return nv;
    });
  };
  const prevUnreadRef = useRef(0);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const matchChatsEligible = !!(user && (isSuperAdmin || isCaptain || user.role === 'vice_captain' || user.club_id));
  const { unread: matchChatsUnread } = useMatchChatsUnread(user, matchChatsEligible);
  const { total: friendsUnread } = useFriendsUnread(user);
  const { count: marketBoardUnread } = useMarketBoardUnread(user);
  const { count: mediationsCount } = useMediationsCount(user, isAdmin || isSuperAdmin);
  const { openChat: openFloatingChat } = useFloatingChat();

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.15, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      playTone(880, 0, 0.12);
      playTone(1100, 0.1, 0.15);
    } catch (e) { /* silent fallback */ }
  }, []);

  const isItalian = i18n.language === 'it';
  const lang = i18n.language;
  const tx = (it, en, de) => lang === 'it' ? it : lang === 'de' ? de : en;
  const isModerator = user?.role === 'moderator';
  const canModerate = isSuperAdmin || isAdmin || isModerator;

  // Check push notification support and subscription status
  useEffect(() => {
    const checkPush = async () => {
      const supported = await isPushSupported();
      setPushSupported(supported);
      if (supported) {
        const subscribed = await isCurrentlySubscribed();
        setPushEnabled(subscribed);
      }
    };
    if (user) checkPush();
  }, [user]);


  const togglePush = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success(isItalian ? 'Notifiche push disattivate' : 'Push notifications disabled');
      } else {
        const sub = await subscribeToPush();
        if (sub) {
          setPushEnabled(true);
          toast.success(isItalian ? 'Notifiche push attivate!' : 'Push notifications enabled!');
        } else {
          toast.error(isItalian ? 'Permesso notifiche negato. Abilita le notifiche nelle impostazioni del browser.' : 'Notification permission denied. Enable in browser settings.');
        }
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      toast.error(isItalian ? 'Errore attivazione push' : 'Push toggle error');
    } finally {
      setPushLoading(false);
    }
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await notificationsApi.list({ unread_only: false });
        setNotifications(response.data.slice(0, 10));
        // Count truly unread (not viewed in this session)
        const unread = response.data.filter(n => !n.read && !viewedNotifications.has(n.notification_id));
        setUnreadCount(unread.length);
        // Play sound when new notifications arrive
        if (unread.length > prevUnreadRef.current) {
          playNotificationSound();
        }
        prevUnreadRef.current = unread.length;
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, viewedNotifications]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const markAsRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      setViewedNotifications(prev => new Set([...prev, id]));
      setNotifications((prev) => prev.map(n => 
        n.notification_id === id ? { ...n, read: true } : n
      ));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    // Update local state FIRST for instant UI feedback
    const allIds = notifications.map(n => n.notification_id);
    setViewedNotifications(prev => new Set([...prev, ...allIds]));
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    prevUnreadRef.current = 0;
    try {
      await notificationsApi.markAllRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationsOpen = (open) => {
    setNotificationsOpen(open);
  };

  const handleNotificationClick = (notif) => {
    // Mark as read (fire and forget)
    notificationsApi.markRead(notif.notification_id).catch(console.error);
    setNotifications((prev) => prev.filter((n) => n.notification_id !== notif.notification_id));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    // Chat-type notifications: open floating mini-chat (Messenger style),
    // do NOT navigate so the user keeps the current page.
    if (notif.type === 'private_message') {
      const fid = notif.data?.friendship_id;
      if (fid) {
        openFloatingChat({
          key: `friend-${fid}`,
          type: 'friend',
          title: notif.data?.from_gamertag || notif.title || 'Messaggio',
          subtitle: 'Chat amico',
          avatar: notif.data?.from_picture || null,
          params: { friendshipId: fid },
        });
        setNotificationsOpen(false);
        return;
      }
    }
    if (notif.type === 'match_chat_message') {
      const mid = notif.data?.match_id;
      if (mid) {
        openFloatingChat({
          key: `match-${mid}`,
          type: 'match',
          title: notif.data?.match_title || notif.title || 'Chat partita',
          subtitle: 'Chat partita',
          params: { matchId: mid },
        });
        setNotificationsOpen(false);
        return;
      }
    }
    if (notif.type === 'market_board_message') {
      const adId = notif.data?.ad_id;
      const otherUserId = notif.data?.other_user_id;
      if (adId && otherUserId) {
        openFloatingChat({
          key: `market-${adId}-${otherUserId}`,
          type: 'market',
          title: notif.data?.from_gamertag || notif.title || 'Calciomercato',
          subtitle: 'Calciomercato',
          params: { adId, otherUserId },
        });
        setNotificationsOpen(false);
        return;
      }
    }

    // Navigate based on notification type
    let targetPath = '/dashboard';
    
    switch (notif.type) {
      case 'transfer_request':
      case 'transfer_request_accepted':
      case 'transfer_request_rejected':
        targetPath = '/club';
        break;
      case 'transfer_offer':
      case 'transfer_accepted':
      case 'transfer_rejected':
      case 'transfer_completed':
        targetPath = '/club';
        break;
      case 'contract_offer':
      case 'contract_accepted':
      case 'contract_rejected':
        targetPath = '/contracts';
        break;
      case 'join_request':
      case 'join_request_accepted':
      case 'join_request_rejected':
      case 'player_joined':
      case 'player_left':
      case 'player_released':
        targetPath = '/club';
        break;
      case 'match_scheduled':
      case 'match_result':
      case 'match_report_pending':
      case 'new_match':
        targetPath = '/matches';
        break;
      case 'coins_purchased':
      case 'coins_received':
      case 'coins_update':
        targetPath = '/shop';
        break;
      case 'championship_started':
      case 'championship_ended':
        targetPath = '/championship';
        break;
      case 'chat_mention':
        targetPath = '/chat';
        break;
      case 'friend_request':
      case 'friend_accepted':
        targetPath = '/friends?tab=requests';
        break;
      case 'private_message': {
        const fid = notif.data?.friendship_id;
        targetPath = fid ? `/friends?tab=friends&open=${fid}` : '/friends?tab=friends';
        break;
      }
      case 'match_chat_message': {
        const mid = notif.data?.match_id;
        targetPath = mid ? `/match-chats?open=${mid}` : '/match-chats';
        break;
      }
      case 'market_board_message':
        targetPath = '/calciomercato';
        break;
      default:
        targetPath = '/dashboard';
        break;
    }
    
    // Navigate after a small delay to let the dropdown close
    setTimeout(() => {
      navigate(targetPath);
    }, 100);
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), show: true },
    { to: '/club', icon: Shield, label: t('nav.club'), show: isCaptain || user?.club_id },
    { to: '/find-club', icon: Search, label: tx('Trova Club', 'Find Club', 'Verein finden'), show: true },
    { to: '/calciomercato', icon: ArrowLeftRight, label: tx('Calciomercato', 'Transfer Market', 'Transfermarkt'), show: !!user, badge: marketBoardUnread },
    { to: '/contracts', icon: FileText, label: tx('Contratti', 'Contracts', 'Vertr\u00e4ge'), show: true },
    { to: '/shop', icon: Coins, label: tx('Shop Coins', 'Coin Shop', 'M\u00fcnzen-Shop'), show: true },
    { to: '/matches', icon: Calendar, label: tx('Partite Campionato', 'Championship Matches', 'Liga-Spiele'), show: true },
    { to: '/championship', icon: Trophy, label: t('nav.championship'), show: true },
    { section: 'tornei' },
    { to: '/tornei', icon: Trophy, label: tx('Tornei', 'Tournaments', 'Turniere'), show: true, indent: true },
    { to: '/tornei-partite', icon: Swords, label: tx('Partite Tornei', 'Tournament Matches', 'Turnier-Spiele'), show: true, indent: true },
    { to: '/albo-d-oro', icon: Crown, label: tx("Albo d'Oro", 'Hall of Fame', 'Ruhmeshalle'), show: true, indent: true },
    { section: 'end' },
    { to: '/stats', icon: BarChart3, label: tx('Statistiche', 'Stats', 'Statistiken'), show: true },
    { to: '/chat', icon: MessageSquare, label: 'Chat', show: true },
    { to: '/match-chats', icon: MessageSquare, label: tx('Messaggi Partite', 'Match Chats', 'Match-Chats'), show: isSuperAdmin || isCaptain || (user?.role === 'vice_captain'), badge: matchChatsUnread },
    { to: '/friends', icon: Users, label: tx('Amici', 'Friends', 'Freunde'), show: !!user, badge: friendsUnread },
    { to: '/regolamento', icon: BookOpen, label: 'Regolamento', show: true },
    { section: 'admin' },
    { to: '/admin/mediazioni', icon: LifeBuoy, label: tx('Mediazioni', 'Mediations', 'Mediation'), show: isAdmin || isSuperAdmin, indent: true, badge: mediationsCount },
    { to: '/moderation', icon: ShieldCheck, label: tx('Moderazione', 'Moderation', 'Moderation'), show: canModerate, indent: true },
    { to: '/admin', icon: Settings, label: t('nav.admin'), show: isAdmin, indent: true },
    { to: '/super-admin', icon: Crown, label: t('nav.superAdmin'), show: isSuperAdmin, indent: true },
    { section: 'end-admin' },
  ];

  return (
    <header className="h-16 bg-[#050508]/95 backdrop-blur-xl border-b border-[#00F0FF]/15 fixed top-0 left-0 right-0 z-50" style={{boxShadow: '0 1px 30px rgba(0,240,255,0.06)'}}>
      <div className="h-full px-4 md:px-6 flex items-center justify-between">
        {/* Left Side - Menu + Logo */}
        <div className="flex items-center gap-3">
          {/* Dropdown Menu */}
          {user && (
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-slate-400 hover:text-[#00F0FF] hover:bg-[#00F0FF]/5"
                  data-testid="main-menu-btn"
                >
                  {menuOpen ? <X size={24} /> : <Menu size={24} />}
                  {!menuOpen && (matchChatsUnread + friendsUnread + (mediationsCount || 0)) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF1744] text-white text-[9px] font-bold border border-[#050508]" data-testid="match-chats-unread-dot">
                      {(matchChatsUnread + friendsUnread + (mediationsCount || 0)) > 9 ? '9+' : (matchChatsUnread + friendsUnread + (mediationsCount || 0))}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className="w-64 bg-[#0a0d15] border-[#00F0FF]/10 p-2"
                sideOffset={8}
              >
                {/* User Info */}
                <div className="px-3 py-3 mb-2 bg-[#050508] border border-[#00F0FF]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#00F0FF]/5 border border-[#00F0FF]/20 flex items-center justify-center overflow-hidden" style={{boxShadow: '0 0 10px rgba(0,240,255,0.1)'}}>
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User size={18} className="text-[#00F0FF]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {user.gamertag ? `${user.gamertag}` : user.name}
                      </p>
                      <p className="text-[10px] text-[#00F0FF] uppercase tracking-widest">{user.role}</p>
                    </div>
                  </div>
                </div>

                {/* Navigation Items */}
                {(() => {
                  const out = [];
                  let inTourney = false;
                  let inAdmin = false;
                  navItems.forEach((item, idx) => {
                    if (item.section === 'tornei') {
                      inTourney = true;
                      out.push(
                        <button
                          key={`section-${idx}`}
                          onClick={toggleTourneyHdr}
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full flex items-center justify-between gap-2 px-3 pt-3 pb-1 border-t border-[#FF00E5]/10 mt-1 text-left hover:bg-[#FF00E5]/5 transition-colors"
                          data-testid="header-toggle-tournaments-group"
                        >
                          <p className="text-[10px] font-bold text-[#FF00E5] uppercase tracking-[0.2em]">
                            {isItalian ? 'Tornei' : 'Tournaments'}
                          </p>
                          {tourneyOpenHdr ? (
                            <ChevronDown size={12} className="text-[#FF00E5]/60" />
                          ) : (
                            <ChevronRight size={12} className="text-[#FF00E5]/60" />
                          )}
                        </button>
                      );
                      return;
                    }
                    if (item.section === 'end') {
                      inTourney = false;
                      out.push(<div key={`section-${idx}`} className="border-b border-[#FF00E5]/10 mb-1" />);
                      return;
                    }
                    if (item.section === 'admin') {
                      // Header admin group only if user has permission for at least one of the admin items
                      const hasAdminAccess = canModerate || isAdmin || isSuperAdmin;
                      if (!hasAdminAccess) return;
                      inAdmin = true;
                      out.push(
                        <button
                          key={`section-${idx}`}
                          onClick={toggleAdminHdr}
                          onMouseDown={(e) => e.preventDefault()}
                          className="w-full flex items-center justify-between gap-2 px-3 pt-3 pb-1 border-t border-[#FFEA00]/10 mt-1 text-left hover:bg-[#FFEA00]/5 transition-colors"
                          data-testid="header-toggle-admin-group"
                        >
                          <p className="text-[10px] font-bold text-[#FFEA00] uppercase tracking-[0.2em]">
                            {isItalian ? 'Amministrazione' : 'Administration'}
                          </p>
                          {adminOpenHdr ? (
                            <ChevronDown size={12} className="text-[#FFEA00]/60" />
                          ) : (
                            <ChevronRight size={12} className="text-[#FFEA00]/60" />
                          )}
                        </button>
                      );
                      return;
                    }
                    if (item.section === 'end-admin') {
                      inAdmin = false;
                      return;
                    }
                    if (inTourney && !tourneyOpenHdr) return;
                    if (inAdmin && !adminOpenHdr) return;
                    if (!item.show) return;
                    out.push(
                      <DropdownMenuItem
                        key={item.to}
                        className={`px-3 py-2.5 text-slate-400 hover:text-white hover:bg-[#00F0FF]/5 cursor-pointer focus:bg-[#00F0FF]/5 focus:text-white ${item.indent ? 'pl-6' : ''}`}
                        onClick={() => {
                          navigate(item.to);
                          setMenuOpen(false);
                        }}
                        data-testid={`nav-${item.to.replace('/', '')}`}
                      >
                        <item.icon size={16} className="mr-3 text-slate-600" />
                        <span className="uppercase tracking-wider text-xs font-medium flex-1">{item.label}</span>
                        {item.badge > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF1744] text-white text-[10px] font-bold">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </DropdownMenuItem>
                    );
                  });
                  return out;
                })()}

                <DropdownMenuSeparator className="bg-[#00F0FF]/10 my-2" />

                {/* Profile */}
                <DropdownMenuItem
                  className="px-3 py-2.5 text-slate-400 hover:text-white hover:bg-[#00F0FF]/5 cursor-pointer focus:bg-[#00F0FF]/5 focus:text-white"
                  onClick={() => {
                    navigate('/profile');
                    setMenuOpen(false);
                  }}
                  data-testid="nav-profile"
                >
                  <User size={16} className="mr-3 text-slate-600" />
                  <span className="uppercase tracking-wider text-xs font-medium">
                    {isItalian ? 'Profilo' : 'Profile'}
                  </span>
                </DropdownMenuItem>

                {/* Logout */}
                <DropdownMenuItem
                  className="px-3 py-2.5 text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/5 cursor-pointer focus:bg-[#FF1744]/5"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut size={16} className="mr-3" />
                  <span className="uppercase tracking-wider text-xs font-medium">{t('nav.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Logo */}
          <NavLink to={user ? '/dashboard' : '/'} className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src="/ept-logo.png" 
              alt="EPT Italy" 
              className="h-10 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 0 6px rgba(0,240,255,0.25))' }}
            />
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-base uppercase tracking-wider text-white leading-none">
                EPT ITALY
              </h1>
              <p className="text-[9px] text-[#00F0FF]/40 uppercase tracking-[0.2em]">eSports League</p>
            </div>
          </NavLink>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Digital Clock */}
          <div className="hidden sm:block">
            <DigitalClock />
          </div>
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-white"
                data-testid="language-selector"
              >
                <Globe size={20} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#151921] border-white/10">
              <DropdownMenuItem
                onClick={() => handleLanguageChange('en')}
                className="text-slate-300 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <span className="mr-2">🇬🇧</span> English
                {i18n.language === 'en' && <Check size={16} className="ml-auto text-[#00E676]" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleLanguageChange('it')}
                className="text-slate-300 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <span className="mr-2">🇮🇹</span> Italiano
                {i18n.language === 'it' && <Check size={16} className="ml-auto text-[#00E676]" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleLanguageChange('de')}
                className="text-slate-300 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <span className="mr-2">🇩🇪</span> Deutsch
                {i18n.language === 'de' && <Check size={16} className="ml-auto text-[#00E676]" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Notifications */}
          {user && (
            <DropdownMenu onOpenChange={handleNotificationsOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-400 hover:text-[#00F0FF] hover:bg-[#00F0FF]/5 relative"
                  data-testid="notifications-btn"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF1744] text-xs flex items-center justify-center text-white font-bold pulse-glow" style={{ boxShadow: '0 0 8px rgba(255,23,68,0.5)' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-[#0a0d15] border-[#00F0FF]/10">
                <div className="p-3 border-b border-[#00F0FF]/10 flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-white uppercase tracking-wider text-sm">
                    {t('notifications.title')}
                  </h3>
                  <div className="flex items-center gap-2">
                    {pushSupported && (
                      <button
                        onClick={togglePush}
                        disabled={pushLoading}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition-all ${
                          pushEnabled
                            ? 'bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 hover:bg-[#00E676]/25'
                            : 'bg-white/5 text-slate-500 border border-white/10 hover:bg-white/10 hover:text-slate-300'
                        }`}
                        data-testid="push-toggle-btn"
                      >
                        {pushLoading ? (
                          <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <BellRing size={11} />
                        )}
                        Push
                      </button>
                    )}
                    {(unreadCount > 0 || notifications.some(n => !n.read)) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        markAllAsRead();
                      }}
                      className="text-xs text-[#2979FF] hover:text-[#2979FF] hover:bg-[#2979FF]/10 px-2 py-1 h-auto"
                      data-testid="mark-all-read-btn"
                    >
                      {isItalian ? 'Segna tutte lette' : 'Mark all read'}
                    </Button>
                  )}
                  </div>
                </div>
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {t('notifications.noNotifications')}
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map((notif) => (
                      <DropdownMenuItem
                        key={notif.notification_id}
                        className={`p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer flex flex-col items-start focus:bg-white/5 ${
                          !notif.read ? 'bg-[#2979FF]/10 border-l-2 border-l-[#2979FF]' : ''
                        }`}
                        onSelect={(e) => {
                          e.preventDefault();
                          handleNotificationClick(notif);
                        }}
                        data-testid={`notification-${notif.notification_id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {!notif.read && (
                            <span className="w-2 h-2 bg-[#2979FF] rounded-full flex-shrink-0" />
                          )}
                          <p className={`text-sm font-medium ${!notif.read ? 'text-white' : 'text-slate-400'}`}>
                            {notif.title}
                          </p>
                        </div>
                        <p className={`text-xs mt-1 whitespace-normal ${!notif.read ? 'text-slate-300' : 'text-slate-500'}`}>
                          {notif.message}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {new Date(notif.created_at).toLocaleDateString(isItalian ? 'it-IT' : 'en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Avatar (visible only on larger screens) */}
          {user && (
            <>
              {/* Personal Coins Balance */}
              <div 
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#FFE600]/5 border border-[#FFE600]/15 cursor-pointer hover:bg-[#FFE600]/10 hover:border-[#FFE600]/25 transition-all duration-150"
                onClick={() => navigate('/shop')}
                data-testid="header-coins-balance"
              >
                <Coins size={14} className="text-[#FFE600]" />
                <span className="text-sm font-bold font-mono text-[#FFE600]">
                  {(user.personal_coins || 0).toLocaleString()}
                </span>
              </div>
              
              {/* User info - larger screens only */}
              <div className="hidden md:flex items-center gap-2 pl-3 border-l border-[#00F0FF]/10">
                <div className="w-8 h-8 bg-[#0a0d15] border border-[#00F0FF]/15 flex items-center justify-center overflow-hidden">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User size={14} className="text-[#00F0FF]/60" />
                  )}
                </div>
                <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                  {user.gamertag ? `${user.gamertag}` : user.name}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
